import React, { useMemo } from 'react';
import { geoNaturalEarth1, geoPath } from 'd3-geo';
import { feature } from 'topojson-client';
import topology from '../data/world-110m.json';

// Fixed internal coordinate space; the SVG scales responsively via viewBox.
const WIDTH = 800;
const HEIGHT = 380;

const EMPTY_FILL = '#f1f5f9'; // slate-100 for countries with no visitors

// Blend from a light indigo to solid indigo based on the value's share of max.
function shade(value, max) {
  if (!value || !max) return EMPTY_FILL;
  const t = Math.min(value / max, 1);
  // from #e0e7ff (224,231,255) to #4f46e5 (79,70,229)
  const r = Math.round(224 + (79 - 224) * t);
  const g = Math.round(231 + (70 - 231) * t);
  const b = Math.round(255 + (229 - 255) * t);
  return `rgb(${r}, ${g}, ${b})`;
}

// values: { [numericIsoId: string]: count }, max: number
// nameFor: (numericId) => display name (for native tooltip)
export default function WorldChoropleth({ values = {}, max = 0, nameFor }) {
  const { features, pathFor } = useMemo(() => {
    const fc = feature(topology, topology.objects.countries);
    const projection = geoNaturalEarth1().fitSize([WIDTH, HEIGHT], fc);
    return { features: fc.features, pathFor: geoPath(projection) };
  }, []);

  return (
    <svg
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      width="100%"
      style={{ height: 'auto' }}
      role="img"
      aria-label="World map of visitors by country"
    >
      {features.map((f) => {
        // Normalise topojson ids like "008" to "8" so they match the value map
        // (which is keyed by parseInt-normalised numeric ISO codes).
        const id = String(parseInt(f.id, 10));
        const value = values[id] || 0;
        const name = (nameFor && nameFor(id)) || f.properties?.name || '';
        return (
          <path
            key={id}
            d={pathFor(f)}
            fill={shade(value, max)}
            stroke="#ffffff"
            strokeWidth={0.5}
          >
            <title>{value ? `${name}: ${value}` : name}</title>
          </path>
        );
      })}
    </svg>
  );
}
